import React from 'react'
import clsx from 'clsx'

import { assetUrl } from 'utils/url'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  color?: 'primary' | 'secondary'
  variant?: 'solid' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  disabled?: boolean
  isLoading?: boolean
}

const variants = {
  outline: 'bg-transparent border',
  solid: 'text-white',
  ghost: 'bg-transparent',
}

const colors = {
  outline: {
    primary: 'text-primary border-primary',
    secondary: 'text-secondary border-secondary',
    disabled: 'text-gray-300 border-gray-300',
  },
  solid: {
    primary: 'text-white bg-primary border-primary',
    secondary: 'text-white bg-secondary border-secondary',
    disabled: 'text-white bg-gray-300',
  },
  ghost: {
    primary: 'text-primary hover:bg-blue-10 focus:bg-blue-10',
    secondary: 'text-secondary hover:bg-yellow-500 focus:bg-yellow-500',
    disabled:
      'text-gray-300 hover:bg-gray-300 hover:text-white focus:bg-gray-300',
  },
}

const sizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-2 text-lg',
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    children,
    className,
    color = 'primary',
    variant = 'solid',
    size = 'md',
    isLoading = false,
    disabled = false,
    ...props
  }) => {
    const buttonDisabled = disabled || isLoading

    const classes = clsx(
      'font-medium rounded-lg text-white block flex items-center justify-center',
      { 'opacity-80 cursor-not-allowed': buttonDisabled },
      variants[variant],
      colors[variant][disabled ? 'disabled' : color],
      sizes[size],
      className
    )
    return (
      <button className={classes} aria-disabled={buttonDisabled} {...props}>
        {isLoading && (
          <img
            src={assetUrl('/icon/circle-loading-white.svg')}
            className="w-[1.25em] h-[1.25em] mr-4 animate-spin"
            alt=""
            aria-hidden
          />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
